import React, { useState, useRef, useEffect, MouseEvent } from 'react';
import './App.css';
import Header from "./components/Header/Header";
import SettingCenter from "./components/SettingCenter/SettingCenter";
import ImageViewer from "./components/ImageViewer/ImageViewer";
import LogViewer from "./components/LogViewer/LogViewer";
import MobileBtns from "./components/MobileBtns/MobileBtns";
import Mask from "./components/Mask/Mask";
import Faq from "./components/Faq/Faq";
import Footer from "./components/Footer/Footer";
import CameraVideo from './components/CameraVideo/CameraVideo';
import Redirect from './components/Redirect/Redirect';
import Dialog from "./components/Dialog/Dialog";
import { device } from "./utils/helpers";
import { useDispatch, useSelector } from 'react-redux';
import { DivRef, InputRef, PreArrRef } from './utils/typesAndMaps';
import { RootStore } from './store';
import { updateDialogState } from './store/features/dialogStatesSlice';

export default function App() {
  const mobileBtnsRef: DivRef = useRef(null);
  const settingsDivRef: DivRef = useRef(null);
  const imageListWrapperRef: DivRef = useRef(null);
  const loadingRef: InputRef = useRef(null);
  const logPreRef: PreArrRef = useRef([]);

  const [isShowSettings, setSettingsVisibility] = useState(false);
  const [isShowImagePanel, setImagePanelVisibility] = useState(false);
  const [isShowRedirectModal, setRedirectModalVisibility] = useState(false);

  const generalStates = useSelector((store: RootStore) => store.generalStates);
  const response = useSelector((store: RootStore) => store.decodeResponse);
  const imagesData = useSelector((store: RootStore) => store.imagesData);
  const dialogStates = useSelector((store: RootStore) => store.dialogStates);

  const dispatch = useDispatch();

  useEffect(() => {
    window.addEventListener("resize", () => {
      if (["ipad", "pc"].includes(device())) {
        imageListWrapperRef.current!.style.display = "block";
        mobileBtnsRef.current!.style.display = "none";
        settingsDivRef.current!.style.display = "block";
        settingsDivRef.current!.style.position = "relative";
        setSettingsVisibility(true);
        setImagePanelVisibility(true);
      } else {
        imageListWrapperRef.current!.style.display = "none";
        mobileBtnsRef.current!.style.display = "flex";
        settingsDivRef.current!.style.display = "none";
        setSettingsVisibility(false);
        setImagePanelVisibility(false);
      }
    });

    document.addEventListener("keydown", (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        const inputs = document.querySelectorAll("input");
        inputs.forEach(input => input.blur());
      }
    });

    if ((navigator.userAgent.match(/Android/i) || navigator.userAgent.match(/iPhone|iPad|iPod/i)) && WebAssembly) {
      setRedirectModalVisibility(true);
    }
  }, []);

  useEffect(() => {
    if (!dialogStates.clickSampleImage && generalStates.curImgIdx === 1) {
      dispatch(updateDialogState({ stateName: "isShowDialog", value: true }));
      dispatch(updateDialogState({ stateName: "clickSampleImage", value: true }));
      // dispatch(updateDialogState({ stateName: "title", value: "Batch Scan Solution Available" }));
      // dispatch(updateDialogState({ stateName: "content", value: "You've selected a sample image demonstrating our batch scan use case. Our Batch Scanning Solution is designed to consistently achieve close to 100% read rate across 100+ barcodes." }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generalStates.curImgIdx, dispatch]);

  useEffect(() => {
    if (!dialogStates.responseCount && generalStates.curImgIdx !== -1 && !imagesData[generalStates.curImgIdx].sampleImg && response.results && response.results.length >= 20) {
      dispatch(updateDialogState({ stateName: "isShowDialog", value: true }));
      dispatch(updateDialogState({ stateName: "responseCount", value: true }));
      // dispatch(updateDialogState({ stateName: "title", value: "Consider Using Our Batch Scan Solution" }));
      // dispatch(updateDialogState({ stateName: "content", value: "You've successfully decoded over 20 barcodes. For businesses who need to scan large batches of inventory and have zero tolerance for inaccuracy, we recommend using our batch scan solution." }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response, dispatch]);

  const showSettings = () => {
    const displayValue = getComputedStyle(settingsDivRef.current!).getPropertyValue('display');
    if (displayValue === "none") {
      settingsDivRef.current!.style.display = "block";
      settingsDivRef.current!.style.position = "absolute";
      mobileBtnsRef.current!.style.display = "none";
      setSettingsVisibility(true);
    }
  }

  const showImagePanel = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const displayValue = getComputedStyle(imageListWrapperRef.current!).getPropertyValue('display');
    if (displayValue === "none") {
      imageListWrapperRef.current!.style.display = "block";
      setImagePanelVisibility(true);
    }
  }

  const hideSettingsOrPanel = () => {
    if (isShowSettings) {
      settingsDivRef.current!.style.display = "none";
      mobileBtnsRef.current!.style.display = "flex";
      setSettingsVisibility(false);
    } else {
      imageListWrapperRef.current!.style.display = "none";
      setImagePanelVisibility(false);
    }
  };

  return (
    <div className="dbr-container">
      {dialogStates.isShowDialog && <Dialog title={dialogStates.title} content={dialogStates.content} />}
      {isShowRedirectModal && <Redirect setRedirectModalVisibility={setRedirectModalVisibility} />}
      {generalStates.cameraState && <CameraVideo />}
      <Header loadingRef={loadingRef} />
      <MobileBtns
        mobileBtnsRef={mobileBtnsRef}
        showSettings={showSettings}
        showImagePanel={showImagePanel}
      />
      {(isShowSettings || isShowImagePanel) && <Mask hideSettingsOrPanel={hideSettingsOrPanel} />}
      <div className="dbr-main">
        <SettingCenter
          settingsDivRef={settingsDivRef}
          hideSettingsOrPanel={hideSettingsOrPanel}
        />
        <ImageViewer
          imageListWrapperRef={imageListWrapperRef}
          loadingRef={loadingRef}
          logPreRef={logPreRef}
        />
        <LogViewer logPreRef={logPreRef} />
      </div>
      <Faq />
      <Footer />
    </div>
  );
}